import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, PackageSearch, XCircle } from "lucide-react";
import { useCart, inr } from "../../store/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-dwc-charcoal outline-none transition-colors focus:border-dwc-green";

const Shell = ({ children, testId }) => (
  <section className="bg-dwc-soft pb-24 pt-32 md:pt-44" data-testid={testId}>
    <div className="mx-auto max-w-2xl px-5">{children}</div>
  </section>
);

const OrderSummaryCard = ({ order }) => (
  <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-6 text-left">
    <p className="flex justify-between text-sm font-bold text-dwc-charcoal/70">
      <span>Order Number</span>
      <span className="font-black text-dwc-green" data-testid="order-number">{order.order_number}</span>
    </p>
    {order.items?.map((i) => (
      <p key={i.slug || i.name} className="mt-2 flex justify-between text-[13px] font-semibold text-dwc-charcoal/70">
        <span className="truncate pr-3">{i.name} × {i.qty}</span>
        <span>{inr(i.price * i.qty)}</span>
      </p>
    ))}
    {order.total != null && (
      <p className="mt-3 flex justify-between border-t border-gray-100 pt-3 text-base font-black text-dwc-charcoal">
        <span>Total</span>
        <span data-testid="order-total">{inr(order.total)}</span>
      </p>
    )}
    {order.payment_method && (
      <p className="mt-2 text-[12px] font-bold uppercase tracking-wide text-dwc-charcoal/50">
        Payment: {order.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}
      </p>
    )}
  </div>
);

export const StoreOrderConfirmed = () => {
  const { state } = useLocation();
  const order = state?.order;
  if (!order)
    return (
      <Shell testId="order-confirmed-page">
        <div className="text-center">
          <p className="text-lg font-extrabold text-dwc-charcoal">No recent order found.</p>
          <Link to="/store/track" className="mt-4 inline-block text-sm font-bold uppercase tracking-wide text-dwc-green">Track an Order</Link>
        </div>
      </Shell>
    );
  return (
    <Shell testId="order-confirmed-page">
      <div className="rounded-[2rem] border border-gray-100 bg-white p-8 text-center md:p-12">
        <CheckCircle2 className="mx-auto h-16 w-16 text-dwc-green" aria-hidden="true" />
        <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-dwc-charcoal">Order Confirmed</h1>
        <p className="mt-2 text-sm text-dwc-charcoal/70">Thank you for shopping with DWC Store. Our team will confirm your order shortly.</p>
        <OrderSummaryCard order={order} />
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/store/track" data-testid="track-order-btn" className="rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white">Track Order</Link>
          <Link to="/store/shop" className="rounded-xl border-2 border-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-dwc-green">Continue Shopping</Link>
        </div>
        <p className="mt-5 text-[11px] text-dwc-charcoal/45">Save your order number and use it with your mobile number to track your order.</p>
      </div>
    </Shell>
  );
};

export const StorePaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderNumber = params.get("order");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 8) {
        if (!cancelled) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/api/store/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          clear();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setState("failed");
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Shell testId="payment-success-page">
      <div className="rounded-[2rem] border border-gray-100 bg-white p-8 text-center md:p-12">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-dwc-green" aria-hidden="true" />
            <h1 className="mt-5 text-2xl font-extrabold text-dwc-charcoal">Confirming your payment...</h1>
            <p className="mt-2 text-sm text-dwc-charcoal/60">Please wait, do not close this page.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-dwc-green" aria-hidden="true" />
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-dwc-charcoal">Payment Successful — Order Confirmed</h1>
            <p className="mt-2 text-sm text-dwc-charcoal/70">Thank you for shopping with DWC Store.</p>
            {orderNumber && (
              <p className="mt-5 rounded-xl bg-dwc-soft px-5 py-4 text-sm font-bold text-dwc-charcoal">
                Order Number: <span className="font-black text-dwc-green" data-testid="paid-order-number">{orderNumber}</span>
              </p>
            )}
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/store/track" className="rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white">Track Order</Link>
              <Link to="/store/shop" className="rounded-xl border-2 border-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-dwc-green">Continue Shopping</Link>
            </div>
          </>
        )}
        {(state === "failed" || state === "error" || state === "timeout") && (
          <>
            <XCircle className="mx-auto h-16 w-16 text-dwc-crimson" aria-hidden="true" />
            <h1 className="mt-5 text-2xl font-extrabold text-dwc-charcoal">
              {state === "timeout" ? "Payment is taking longer than expected" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-sm text-dwc-charcoal/70">
              {state === "timeout"
                ? "If money was deducted, your order will be confirmed automatically. You can check its status with Track Order."
                : "Your payment was not completed. You can try again from your cart."}
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/store/cart" className="rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white">Back to Cart</Link>
              <Link to="/store/track" className="rounded-xl border-2 border-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-dwc-green">Track Order</Link>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
};

const STATUS_LABEL = { placed: "Order Placed", confirmed: "Confirmed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };

export const StoreTrack = () => {
  const [form, setForm] = useState({ order_number: "", mobile: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const r = await axios.get(`${API}/api/store/orders/track`, { params: form });
      setOrder(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Order not found.");
    }
    setLoading(false);
  };

  return (
    <Shell testId="track-order-page">
      <div className="rounded-[2rem] border border-gray-100 bg-white p-8 md:p-10">
        <PackageSearch className="h-10 w-10 text-dwc-green" aria-hidden="true" />
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-dwc-charcoal">Track Your Order</h1>
        <p className="mt-2 text-sm text-dwc-charcoal/65">Enter your order number and the mobile number used at checkout.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input required value={form.order_number} onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value }))} placeholder="Order Number (e.g. DWCA1B2C3)" aria-label="Order number" data-testid="track-order-input" className={inputCls} />
          <input required value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} placeholder="Mobile Number" aria-label="Mobile number" data-testid="track-mobile-input" className={inputCls} />
          <button type="submit" disabled={loading} data-testid="track-submit-btn" className="w-full rounded-xl bg-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-dwc-green-dark disabled:opacity-60">
            {loading ? "Searching..." : "Track Order"}
          </button>
        </form>
        {error && <p className="mt-4 rounded-lg bg-dwc-crimson/[0.06] px-4 py-3 text-sm font-bold text-dwc-crimson" data-testid="track-error">{error}</p>}
        {order && (
          <div className="mt-6" data-testid="track-result">
            <p className="rounded-xl bg-dwc-soft px-5 py-4 text-sm font-bold text-dwc-charcoal">
              Status: <span className="font-black text-dwc-green" data-testid="track-status">{STATUS_LABEL[order.order_status] || order.order_status}</span>
              <span className="ml-3 text-[12px] font-bold uppercase tracking-wide text-dwc-charcoal/50">
                Payment: {order.payment_status === "cod_pending" ? "COD" : order.payment_status}
              </span>
            </p>
            <OrderSummaryCard order={order} />
          </div>
        )}
      </div>
    </Shell>
  );
};

export default StoreOrderConfirmed;
